<template>
  <section class="i-how-it-works-upsell">
    <div class="container">
      <div class="label mb-3">How it works</div>
      <h2 class="headline">"Can {{ timing }} minutes actually change anything?"</h2>
      <p
        class="body-lg"
        style="color: var(--text-body); margin-top: 12px; max-width: 580px"
      >
        {{ timing }} minutes feels short. It is — because the session is engineered, not improvised. Your coach reviews
        your report <em>before</em> the call starts. Here's the breakdown.
      </p>

      <div class="timeline mt-8">
        <div
          v-for="step in steps"
          :key="step.time"
          class="timeline__row"
        >
          <div class="timeline__time-col">
            <div class="timeline__time">{{ step.time }}</div>
            <div class="timeline__time-label">min</div>
          </div>
          <div class="timeline__content">
            <div class="timeline__title">{{ step.title }}</div>
            <div class="timeline__body">{{ step.body }}</div>
          </div>
        </div>
      </div>

      <!-- Purple CTA Banner -->
      <iCtaBanner
        :high-pricing="highPricing"
        :pricing="pricing"
        style="margin-top: 40px"
        :headline="`$${pricing} now for a median $25.4k Salary Increase?`"
        sub="The math doesn't require persuasion. Ready to make moves?"
        @book="$emit('book')"
      />
      <div
        v-if="!paymentProcessing"
        class="skip"
        @click="$emit('skip')"
      >
        No thanks - I'm fine figuring it out the hard way
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import iCtaBanner from "./CtaBanner.vue"

defineEmits(["book", "skip"])

// PROPS
defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  },
  highPricing: {
    type: String,
    default: "149.90"
  },
  paymentProcessing: {
    type: Boolean,
    default: false
  }
})

// COMPUTED
const steps = [
  {
    time: "0–5",
    title: "You brief them on your situation",
    body: "Your coach has already read your report. You use the first 5 minutes to fill in what the report can't show — what you've tried, what's blocking you, what a good outcome looks like in your terms."
  },
  {
    time: "5–22",
    title: "They tell you exactly what to do",
    body: "This is the session. Specific title variants to target. The exact positioning change that will double your recruiter response rate. The two skills gaps showing up in your profile. The companies most likely to hire you in the next 90 days. This is the part that takes most people 3–6 months to figure out on their own — or never."
  },
  {
    time: "22–30",
    title: "Your plan is drafted live on the call",
    body: "Your coach builds your written action plan on-screen while you watch. You see the specific steps, the exact wording changes, and the 30-day timeline. You leave the call with a document, not a feeling."
  }
]
</script>

<style lang="scss" scoped>
.i-how-it-works-upsell {
  .timeline {
    margin-top: 36px;

    &__row {
      display: grid;
      grid-template-columns: 80px 1fr;
      gap: 20px;

      &:not(:first-child) {
        .timeline {
          &__content {
            border-top: 1px solid var(--border);
            padding-top: 22px;
          }
        }
      }
    }

    &__time-col {
      // left column
    }

    &__content {
      padding-bottom: 22px;
    }

    &__time {
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--purple);
      line-height: 1;
      padding-top: 3px;
      white-space: nowrap;
    }

    &__time-label {
      font-size: 0.7rem;
      color: var(--muted);
      font-weight: 600;
      margin-top: 2px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    &__title {
      font-weight: 700;
      font-size: 0.95rem;
      color: var(--text);
      margin-bottom: 4px;
    }

    &__body {
      font-size: 0.875rem;
      color: var(--text-body);
      line-height: 1.65;
    }
  }
}
</style>
